// The secondary view: rows grouped by phase then workstream, inline edit of status,
// owner and dates, multi-select bulk edit, sorting. No spreadsheet beyond that.
import { Fragment, useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from 'lucide-react';
import {
  PRIORITY_LABEL,
  STATUS_LABEL,
  STATUS_ORDER,
  isOverdue,
  useTaskActions,
  type Priority,
  type Task,
  type TaskStatus,
  type TrackerData,
} from '@/lib/tracker';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/field';
import { Avatar } from '@/components/tracker/bits';
import { useOpenTask } from '@/components/tracker/TaskPanel';

interface Props {
  programId: string;
  data: TrackerData;
  tasks: Task[];
  isAdmin: boolean;
  canEditProgress: (t: Task) => boolean;
  sorting: SortingState;
  onSortingChange: (s: SortingState) => void;
}

export function ListView({ programId, data, tasks, isAdmin, canEditProgress, sorting, onSortingChange }: Props) {
  const { patch, bulk } = useTaskActions(programId);
  const open = useOpenTask();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const memberName = (id: string | null) => data.members.find((m) => m.userId === id)?.name;

  const columns = useMemo<ColumnDef<Task>[]>(
    () => [
      {
        id: 'select',
        header: '',
        enableSorting: false,
        size: 32,
        cell: ({ row }) =>
          isAdmin && (
            <input
              type="checkbox"
              aria-label={`Select ${row.original.title}`}
              className="size-4 accent-[var(--brand)]"
              checked={selected.has(row.original.id)}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(row.original.id);
                else next.delete(row.original.id);
                setSelected(next);
              }}
            />
          ),
      },
      {
        accessorKey: 'title',
        header: 'Task',
        cell: ({ row }) => (
          <button className="truncate text-left hover:underline" onClick={() => open(row.original.id)}>
            {row.original.title}
          </button>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        sortingFn: (a, b) => STATUS_ORDER.indexOf(a.original.status) - STATUS_ORDER.indexOf(b.original.status),
        cell: ({ row }) => (
          <Select
            aria-label="Status"
            className="h-7 w-32 text-xs"
            value={row.original.status}
            disabled={!canEditProgress(row.original)}
            onChange={(e) =>
              patch.mutate({
                id: row.original.id,
                patch: { status: e.target.value as TaskStatus },
                undoLabel: `Status set to ${STATUS_LABEL[e.target.value as TaskStatus]}.`,
              })
            }
          >
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        ),
      },
      {
        id: 'owner',
        header: 'Owner',
        size: 180,
        accessorFn: (t) => memberName(t.ownerId) ?? '',
        cell: ({ row }) =>
          isAdmin ? (
            <Select
              aria-label="Owner"
              className="h-7 w-40 text-xs"
              value={row.original.ownerId ?? ''}
              onChange={(e) =>
                patch.mutate({
                  id: row.original.id,
                  patch: { ownerId: e.target.value || null },
                  undoLabel: 'Owner changed.',
                })
              }
            >
              <option value="">Unassigned</option>
              {data.members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name}
                </option>
              ))}
            </Select>
          ) : (
            <span className="flex items-center gap-1.5 text-xs">
              <Avatar name={memberName(row.original.ownerId)} size={20} />
              {memberName(row.original.ownerId) ?? 'Unassigned'}
            </span>
          ),
      },
      ...(['startDate', 'dueDate'] as const).map((key): ColumnDef<Task> => ({
        accessorKey: key,
        header: key === 'startDate' ? 'Start' : 'Due',
        size: 140,
        sortingFn: (a, b) => (a.original[key] ?? '9999').localeCompare(b.original[key] ?? '9999'),
        cell: ({ row }) =>
          isAdmin ? (
            <Input
              type="date"
              aria-label={key === 'startDate' ? 'Start date' : 'Due date'}
              className={cn(
                'h-7 w-34 text-xs',
                key === 'dueDate' && isOverdue(row.original, data.today) && 'border-bad text-bad',
              )}
              defaultValue={row.original[key] ?? ''}
              key={row.original[key] ?? 'none'}
              onBlur={(e) =>
                (e.target.value || null) !== row.original[key] &&
                patch.mutate({
                  id: row.original.id,
                  patch: { [key]: e.target.value || null },
                  undoLabel: 'Date changed.',
                })
              }
            />
          ) : (
            <span
              className={cn(
                'text-xs tabular',
                key === 'dueDate' && isOverdue(row.original, data.today) ? 'font-medium text-bad' : 'text-muted',
              )}
            >
              {row.original[key] ?? '·'}
            </span>
          ),
      })),
      {
        accessorKey: 'priority',
        header: 'Priority',
        size: 90,
        sortingFn: (a, b) =>
          ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].indexOf(a.original.priority) -
          ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].indexOf(b.original.priority),
        cell: ({ row }) => <span className="text-xs text-muted">{PRIORITY_LABEL[row.original.priority]}</span>,
      },
      {
        accessorKey: 'percent',
        header: '%',
        size: 60,
        cell: ({ row }) => <span className="text-xs tabular text-muted">{row.original.percent}</span>,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, selected, isAdmin],
  );

  const table = useReactTable({
    data: tasks,
    columns,
    state: { sorting },
    onSortingChange: (u) => onSortingChange(typeof u === 'function' ? u(sorting) : u),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (t) => t.id,
  });

  // Group the (sorted) rows by phase, then workstream.
  const rows = table.getRowModel().rows;
  const groups = data.phases
    .map((p) => {
      const pr = rows.filter((r) => r.original.phaseId === p.id);
      const subs = [
        ...p.workstreams.map((w) => ({
          key: w.id,
          label: w.name,
          rows: pr.filter((r) => r.original.workstreamId === w.id),
        })),
        {
          key: `${p.id}:none`,
          label: p.workstreams.length ? 'No workstream' : '',
          rows: pr.filter((r) => !r.original.workstreamId),
        },
      ].filter((s) => s.rows.length);
      return { phase: p, count: pr.length, subs };
    })
    .filter((g) => g.count);

  const toggle = (k: string) => {
    const next = new Set(collapsed);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setCollapsed(next);
  };

  return (
    <div className="rounded-lg border border-border bg-surface shadow-xs">
      {isAdmin && selected.size > 0 && (
        <BulkBar
          count={selected.size}
          members={data.members}
          onApply={(p) => bulk.mutate({ ids: [...selected], patch: p }, { onSuccess: () => setSelected(new Set()) })}
          onClear={() => setSelected(new Set())}
        />
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] table-fixed text-[13px]">
          <thead className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-subtle">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    style={{ width: h.column.id === 'title' ? undefined : h.getSize() }}
                    className="px-2 py-2 first:pl-4"
                  >
                    {h.column.getCanSort() ? (
                      <button
                        className="inline-flex items-center gap-1 uppercase hover:text-text"
                        onClick={h.column.getToggleSortingHandler()}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : h.column.getIsSorted() === 'desc' ? (
                          <ArrowDown className="size-3" />
                        ) : null}
                      </button>
                    ) : (
                      flexRender(h.column.columnDef.header, h.getContext())
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.phase.id}>
                <tr className="border-b border-border bg-surface-2/60">
                  <td colSpan={columns.length} className="px-4 py-1.5">
                    <button
                      className="flex items-center gap-1.5 text-[12.5px] font-semibold"
                      onClick={() => toggle(g.phase.id)}
                    >
                      {collapsed.has(g.phase.id) ? (
                        <ChevronRight className="size-3.5" />
                      ) : (
                        <ChevronDown className="size-3.5" />
                      )}
                      {g.phase.number}. {g.phase.name}
                      <span className="font-normal text-subtle">· {g.count}</span>
                    </button>
                  </td>
                </tr>
                {!collapsed.has(g.phase.id) &&
                  g.subs.map((s) => (
                    <Fragment key={s.key}>
                      {s.label && (
                        <tr className="border-b border-border">
                          <td colSpan={columns.length} className="py-1 pl-10 text-[11.5px] font-medium text-muted">
                            <button className="flex items-center gap-1" onClick={() => toggle(s.key)}>
                              {collapsed.has(s.key) ? (
                                <ChevronRight className="size-3" />
                              ) : (
                                <ChevronDown className="size-3" />
                              )}
                              {s.label} <span className="text-subtle">· {s.rows.length}</span>
                            </button>
                          </td>
                        </tr>
                      )}
                      {!collapsed.has(s.key) &&
                        s.rows.map((r) => (
                          <tr
                            key={r.id}
                            className={cn(
                              'border-b border-border hover:bg-surface-2/40',
                              selected.has(r.id) && 'bg-brand-soft/40',
                            )}
                          >
                            {r.getVisibleCells().map((c) => (
                              <td key={c.id} className="truncate px-2 py-1.5 first:pl-4">
                                {flexRender(c.column.columnDef.cell, c.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </Fragment>
                  ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        {!groups.length && <p className="py-10 text-center text-sm text-muted">No tasks match these filters.</p>}
      </div>
    </div>
  );
}

function BulkBar({
  count,
  members,
  onApply,
  onClear,
}: {
  count: number;
  members: Array<{ userId: string; name: string }>;
  onApply: (p: { status?: TaskStatus; ownerId?: string | null; dueDate?: string | null; priority?: Priority }) => void;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-brand-soft/40 px-4 py-2 text-[13px]">
      <span className="font-medium">{count} selected</span>
      <Select
        aria-label="Set status"
        className="h-7 w-36 text-xs"
        value=""
        onChange={(e) => e.target.value && onApply({ status: e.target.value as TaskStatus })}
      >
        <option value="">Set status…</option>
        {STATUS_ORDER.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABEL[s]}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Set owner"
        className="h-7 w-40 text-xs"
        value=""
        onChange={(e) => e.target.value && onApply({ ownerId: e.target.value === 'none' ? null : e.target.value })}
      >
        <option value="">Set owner…</option>
        <option value="none">Unassigned</option>
        {members.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.name}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Set priority"
        className="h-7 w-36 text-xs"
        value=""
        onChange={(e) => e.target.value && onApply({ priority: e.target.value as Priority })}
      >
        <option value="">Set priority…</option>
        {(Object.keys(PRIORITY_LABEL) as Priority[]).map((p) => (
          <option key={p} value={p}>
            {PRIORITY_LABEL[p]}
          </option>
        ))}
      </Select>
      <Input
        type="date"
        aria-label="Set due date"
        className="h-7 w-36 text-xs"
        onChange={(e) => e.target.value && onApply({ dueDate: e.target.value })}
      />
      <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
        Clear selection
      </Button>
    </div>
  );
}
